import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { certificateAPI } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  XCircle,
  Award,
  Calendar,
  BookOpen,
  Star,
  Search,
  Loader2,
  Shield,
} from "lucide-react";

export default function VerifyPage() {
  const { verificationId: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const queryId = searchParams.get("id");
  
  const [verificationId, setVerificationId] = useState(paramId || queryId || "");
  const [inputId, setInputId] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (verificationId) {
      verifyCertificate(verificationId);
    }
  }, [verificationId]);

  const verifyCertificate = async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const res = await certificateAPI.verify(id);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Certificate not found. Please check the verification ID and try again.");
      } else {
        setError("An error occurred while verifying the certificate.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleManualVerify = (e) => {
    e.preventDefault();
    if (inputId.trim()) {
      setVerificationId(inputId.trim().toUpperCase());
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/axentia-logo.png"
              alt="Axentia Global"
              className="h-10 w-auto"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            <div>
              <h1 className="text-lg font-semibold text-[#005d6c] tracking-tight font-heading">
                AXENTIA GLOBAL
              </h1>
              <p className="text-[10px] text-slate-500 uppercase tracking-widest">
                Executive Search Academy
              </p>
            </div>
          </div>
          <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200">
            <Shield className="w-3 h-3 mr-1" />
            Certificate Verification
          </Badge>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-10">
          <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-2">
            Verify Certification
          </h2>
          <p className="text-sm text-slate-500">
            Enter a certificate verification ID to confirm authenticity
          </p>
        </div>

        {/* Search Form */}
        {!result && (
          <form onSubmit={handleManualVerify} className="mb-10">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  value={inputId}
                  onChange={(e) => setInputId(e.target.value.toUpperCase())}
                  placeholder="Enter Verification ID (e.g., ABC123DEF456)"
                  className="pl-10 h-12 text-base uppercase tracking-wider rounded-sm"
                  data-testid="verification-id-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !inputId.trim()}
                className="h-12 px-6 bg-[#005d6c] hover:bg-[#004a56] text-white rounded-sm"
                data-testid="verify-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Verify"
                )}
              </Button>
            </div>
          </form>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-[#005d6c] mx-auto mb-4" />
            <p className="text-sm text-slate-500">Verifying certificate...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="bg-red-50 border border-red-200 rounded-sm p-8 text-center" data-testid="verification-error">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              Verification Failed
            </h3>
            <p className="text-sm text-red-600 mb-6">{error}</p>
            <Button
              variant="outline"
              onClick={() => {
                setError(null);
                setVerificationId("");
                setInputId("");
              }}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Success State */}
        {result && !loading && (
          <div className="space-y-6" data-testid="verification-success">
            {/* Verified Badge */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 text-center">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h3 className="text-xl font-semibold text-emerald-800 mb-1">
                Certificate Verified
              </h3>
              <p className="text-sm text-emerald-600">
                This certification is authentic and valid
              </p>
            </div>

            {/* Certificate Details */}
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden">
              {/* Header with Logo */}
              <div className="bg-gradient-to-r from-[#005d6c] to-[#004a56] px-6 py-5 text-center">
                <img
                  src="/axentia-logo.png"
                  alt="Axentia Global"
                  className="h-8 w-auto mx-auto mb-2 brightness-0 invert"
                  onError={(e) => {
                    e.target.parentElement.innerHTML = `
                      <h4 class="text-white font-heading text-lg font-semibold">AXENTIA GLOBAL</h4>
                      <p class="text-white/70 text-xs">Executive Search Academy</p>
                    `;
                  }}
                />
                <h4 className="text-white font-heading text-lg font-semibold">
                  AXENTIA GLOBAL
                </h4>
                <p className="text-white/70 text-xs uppercase tracking-wider">
                  Executive Search Academy
                </p>
              </div>

              {/* Certificate Info */}
              <div className="p-6 space-y-5">
                {/* Consultant Name */}
                <div className="text-center pb-5 border-b border-slate-100">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">
                    Certified Consultant
                  </p>
                  <h3 className="text-2xl font-heading font-bold text-slate-900">
                    {result.full_name}
                  </h3>
                </div>

                {/* Certification Title */}
                <div className="text-center pb-5 border-b border-slate-100">
                  <div className="inline-flex items-center gap-2 bg-[#005d6c]/10 text-[#005d6c] px-4 py-2 rounded-sm">
                    <Award className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {result.certification_title}
                    </span>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-sm p-4 text-center">
                    <Star className="w-5 h-5 text-[#005d6c] mx-auto mb-2" />
                    <p className="text-xs text-slate-500 mb-1">Promotion Level</p>
                    <p className="text-sm font-semibold text-slate-900">
                      L{result.promotion_level}: {result.promotion_title}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-sm p-4 text-center">
                    <Calendar className="w-5 h-5 text-[#005d6c] mx-auto mb-2" />
                    <p className="text-xs text-slate-500 mb-1">Completion Date</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {formatDate(result.issued_date)}
                    </p>
                  </div>
                  <div className="bg-slate-50 rounded-sm p-4 text-center col-span-2">
                    <BookOpen className="w-5 h-5 text-[#005d6c] mx-auto mb-2" />
                    <p className="text-xs text-slate-500 mb-1">Modules Completed</p>
                    <p className="text-sm font-semibold text-slate-900">
                      {result.modules_completed} of 20 Core Modules
                    </p>
                  </div>
                </div>

                {/* Verification ID */}
                <div className="pt-4 border-t border-slate-100 text-center">
                  <p className="text-xs text-slate-400 mb-1">Verification ID</p>
                  <code className="text-sm font-mono text-slate-600 bg-slate-100 px-3 py-1 rounded">
                    {result.verification_id}
                  </code>
                </div>
              </div>
            </div>

            {/* Verify Another */}
            <div className="text-center">
              <Button
                variant="outline"
                onClick={() => {
                  setResult(null);
                  setVerificationId("");
                  setInputId("");
                }}
                className="text-slate-600"
              >
                Verify Another Certificate
              </Button>
            </div>
          </div>
        )}

        {/* Info Section (when no search yet) */}
        {!result && !error && !loading && !verificationId && (
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-6 text-center">
            <Shield className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <h4 className="text-sm font-medium text-slate-700 mb-2">
              Authentic Verification
            </h4>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              Axentia Global certifications include a unique verification ID that can be used
              to confirm the authenticity of any certificate issued through our Executive Search Academy.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs text-slate-400">
            © {new Date().getFullYear()} Axentia Global. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
